import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError, BaseResponseEntities } from 'app/shared/reducers/reducer.utils';
import { IProgressLog, defaultValue } from 'app/shared/model/progress-log.model';
import { apiOkr } from 'app/config/axios-interceptor';

const initialState: EntityState<IProgressLog> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/progress-log';
const apiCheckInUrl = 'api/okr/checkin';
const apiActivitiesUrl = 'api/okr/activity';

// Actions
interface IQueryParamsProgressLog extends IQueryParams {
  reportObjectType?: number;
  reportObjectId?: string;
}

export const getEntities = createAsyncThunk(
  'progressLog/fetch_entity_list',
  async ({ searchText, page, size, sort, filters, reportObjectType, reportObjectId }: IQueryParamsProgressLog) => {
    return apiOkr.get<BaseResponseEntities<IProgressLog>>(apiUrl, {
      params: {
        searchText,
        page,
        size,
        filters: filters && JSON.stringify(filters),
        sort: sort && sort,
        reportObjectType,
        reportObjectId,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'progressLog/create_entity',
  async (entity: IProgressLog, thunkAPI) => {
    const result = await apiOkr.post<IProgressLog>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);
type QueryParams = {
  id?: string;
  checkInDetailBy?: number;
};
export const getKrCheckInQual = createAsyncThunk(
  'progressLog/fetch_list_kr_check_in_qual',
  async ({ id, checkInDetailBy }: QueryParams) => {
    return apiOkr.get<any>(`/${apiCheckInUrl}/checkin-qualitative-result/${id}?checkinDetailBy=${checkInDetailBy}`);
  },
  { serializeError: serializeAxiosError },
);

export const getKrCheckInQuanti = createAsyncThunk(
  'progressLog/fetch_list_kr_check_in_quanti',
  async ({ id, checkInDetailBy }: QueryParams) => {
    return apiOkr.get<any>(`/${apiCheckInUrl}/checkin-quantitative-result/${id}?checkinDetailBy=${checkInDetailBy}`);
  },
  { serializeError: serializeAxiosError },
);

export const getResultCheckInActivities = createAsyncThunk(
  'progressLog/fetch_list_result_check_in_activities',
  async (id: string) => {
    return apiOkr.get<any>(`/${apiActivitiesUrl}/checkin-result/${id}`);
  },
  { serializeError: serializeAxiosError },
);
// slice

export const ProgressLogSlice = createEntitySlice({
  name: 'progressLog',
  initialState,
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.items,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isFulfilled(getResultCheckInActivities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(createEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getKrCheckInQual, getKrCheckInQuanti), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entity: data,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isFulfilled(createEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getKrCheckInQual, getKrCheckInQuanti), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addMatcher(isPending(getEntities, getResultCheckInActivities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ProgressLogSlice.actions;

// Reducer
export default ProgressLogSlice.reducer;
